import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import Bluebird from 'bluebird'
import { Maybe } from 'graphql/jsutils/Maybe'
import hash from 'object-hash'
import { Durations } from '@/constants/durations'
import { fetchBoxOfficeFilms } from '@/organisms/BoxOfficeRail'
import { fetchTitlesForPage, isRequiresTitleMapOnPage } from '@/page/TitleCollection'
import { Header, Page } from '@/types/codegen-contentful'
import { isStagingEnvironment } from '@/utils/environment-utils'
import { logger } from '@/utils/logging'
import { omitUndefineds } from '@/utils/object'
import { isDefined } from '@/utils/primitives/generics'
import { createWebClient } from '../ApolloClient'
import { cacheService } from '../CacheService'
import { getContentCollections } from '../CollectionsService'
import { getComingSoonCollection } from '../ComingSoonService'
import {
  formatTitleMap,
  CatalogTitleMap,
  isTitleLinkedToEpisodeWatchPage,
} from '../ContentCatalog/contentCatalogService'
import { findHydraExternalId, findTitleProjectSlug } from '../ContentCatalog/hydra'
import { CatalogTitle } from '../ContentCatalog/types'
import { getFranchiseBySlug } from '../FranchiseService'
import { getAllMovies, getAllShows } from '../NowStreamingService'
import { isInGuildAccess } from '../ProjectPhaseService'
import { getEpisode, getProjectAbout, getWatchableContentByGuid } from '../ProjectsService/ProjectsService'
import { getStartWatchingEpisodes } from './dataSources'
import { getSneakPeekEpisodes } from './dataSources'
import { getCollectionDataSources } from './mapCollectionDataSources'
import type { PageDataContext } from './types'
import { isCollectionDisplay, isFranchiseComponent, isHasTypeName } from './validations'

/** The Contentful sys.id for the Header that displays the Box Office Rail */
const BOX_OFFICE_HEADER_ID = '7kdYlmOQParCYfeKJHasV2'

/** The collection ID used by the Box Office Rail */
const BOX_OFFICE_COLLECTION_ID = '5c7ee724-4deb-4588-9850-e7fe305501cf'

/**
 * Type guard to check if content is a Header
 */
function isHeader(content: unknown): content is Header {
  return isHasTypeName(content) && content.__typename === 'Header'
}

export interface PageDataContextResult extends PageDataContext {
  allQueriesSucceeded: boolean
}

export async function fetchPageDataContext(
  page: Maybe<Page>,
  preview: boolean = false,
  locale: string = 'en',
  client: ApolloClient<NormalizedCacheObject>,
): Promise<PageDataContextResult | null> {
  if (!page || !page.contentCollection?.items || page.contentCollection?.items.length === 0) {
    return {
      allQueriesSucceeded: true,
      preview,
    }
  }

  const cacheKey = cacheService.genCacheKey('renderService.fetchPageDataContext', `${locale}-${preview}-${page.sys.id}`)
  const cached = await cacheService.get(cacheKey)
  if (cached) {
    return JSON.parse(cached)
  }

  const pageContext = await fetchDataSources(page, locale, preview, client)
  const context = omitUndefineds(pageContext)
  await cacheService.set(cacheKey, JSON.stringify(context), { expireInSeconds: Durations.FIFTEEN_MINUTES_IN_SECONDS })

  return context
}

async function fetchDataSources(
  page: Page,
  locale: string,
  preview: boolean,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<PageDataContextResult> {
  const dataSources = getCollectionDataSources(page)
  const pageContext: PageDataContextResult = {
    allQueriesSucceeded: true,
    preview,
  }

  const promises: Promise<void>[] = Array.from(dataSources).map((dataSource) =>
    handleDataSource(dataSource, pageContext, page, client),
  )

  promises.push(
    (async () => {
      if (isRequiresTitleMapOnPage(page)) {
        const titles = await fetchTitlesForPage(page)
        pageContext['title-map'] = await getValidatedTitleMap(titles)
        if (titles.length === 0) pageContext.allQueriesSucceeded = false
      }
    })(),
  )

  promises.push(fetchFranchiseComponent(page, pageContext))
  promises.push(fetchCollectionDisplay(page, pageContext, client))
  promises.push(fetchBoxOfficeHeaderData(page, pageContext, client))

  await Promise.all(promises)

  return pageContext
}

/**
 * Fetches box office data for Headers that display the BoxOfficeRail
 */
async function fetchBoxOfficeHeaderData(
  page: Page,
  pageContext: PageDataContextResult,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  const boxOfficeHeader = page.contentCollection?.items.find(
    (item) => isHeader(item) && item.sys.id === BOX_OFFICE_HEADER_ID,
  )

  if (!boxOfficeHeader) return

  const films = await fetchBoxOfficeFilms(BOX_OFFICE_COLLECTION_ID, client)
  if (films) {
    pageContext['box-office-films'] = {
      [BOX_OFFICE_COLLECTION_ID]: films,
    }
  }
}

async function fetchFranchiseComponent(page: Page, pageContext: PageDataContextResult): Promise<void> {
  const franchiseComponent = page.contentCollection?.items.find(isFranchiseComponent)
  if (!franchiseComponent || !franchiseComponent.franchiseSlug) return

  const franchise = await getFranchiseBySlug(franchiseComponent.franchiseSlug, true)
  if (franchise) {
    pageContext['franchiseComponent'] = franchiseComponent
    pageContext['franchise'] = franchise
  } else {
    pageContext.allQueriesSucceeded = false
  }
}

async function fetchCollectionDisplay(
  page: Page,
  pageContext: PageDataContextResult,
  client?: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  const collectionDisplayComponents = page.contentCollection?.items.filter(isCollectionDisplay)
  if (!collectionDisplayComponents || collectionDisplayComponents.length === 0) return

  const ids = new Set<string>()

  for (const component of collectionDisplayComponents) {
    const contentCollection = component?.contentCollection
    if (contentCollection && contentCollection.title && contentCollection.contentCollectionId) {
      ids.add(contentCollection.contentCollectionId)
    }
  }

  const federationClient = client || createWebClient()

  const collectionDisplayData = await getContentCollections(Array.from(ids), federationClient)
  if (collectionDisplayData) {
    pageContext['content-collection'] = collectionDisplayData
  } else {
    pageContext.allQueriesSucceeded = false
  }
}

/**
 *
 * The data coming back from the Content Catalog is, unfortunately, often invalid.
 * We're patching this on the front-end for now by detecting and removing the invalid titles here.
 * @returns
 */
export async function getValidatedTitleMap(titles: CatalogTitle[]): Promise<CatalogTitleMap> {
  const ids = titles.map((t) => t.id).sort()
  const hashed = hash(ids, { algorithm: 'md5' })
  const cacheKey = cacheService.genCacheKey('renderService.getValidatedTitleMap', `title-ids-hash-${hashed}`)
  const cached = await cacheService.get(cacheKey)
  if (cached) {
    return JSON.parse(cached)
  }

  const client = createWebClient()
  const validated = await Bluebird.map(
    titles,
    async (title) => {
      const projectSlug = findTitleProjectSlug(title)

      if (!projectSlug) return

      try {
        const guid = findHydraExternalId(title)
        if (guid && isTitleLinkedToEpisodeWatchPage(title)) {
          return await validateEpisodeTitle(title, guid, projectSlug, client)
        } else {
          const response = await getProjectAbout({ slug: projectSlug }, client)
          if (response && response.slug === projectSlug) {
            // Fetch watchable content to check for guild access via watchableReasons
            const watchableContent = await getWatchableContentByGuid(response?.title?.id, client)
            const isInEarlyAccess = isInGuildAccess(watchableContent)
            return { ...title, isInEarlyAccess }
          } else {
            reportWarning('project', title)
            return
          }
        }
      } catch (err) {
        reportWarning('episode', title, err as Error)
        return
      }
    },
    { concurrency: 3 },
  )

  const map = formatTitleMap(validated.filter(isDefined))
  await cacheService.set(cacheKey, JSON.stringify(map), { expireInSeconds: Durations.FIFTEEN_MINUTES_IN_SECONDS })

  return map
}

async function validateEpisodeTitle(
  title: CatalogTitle,
  guid: string,
  projectSlug: string,
  client: ReturnType<typeof createWebClient>,
): Promise<CatalogTitle | undefined> {
  const response = await getEpisode({ guid, projectSlug }, client)

  if (response && response.episode) {
    const earlyAccessDate = response.episode?.earlyAccessDate ? new Date(response.episode.earlyAccessDate) : null
    const publiclyAvailableDate = response.episode?.publiclyAvailableDate
      ? new Date(response.episode.publiclyAvailableDate)
      : null
    if (earlyAccessDate) {
      const now = new Date()
      const isInEarlyAccess = Boolean(
        !publiclyAvailableDate ||
          (earlyAccessDate && earlyAccessDate > now && publiclyAvailableDate && publiclyAvailableDate < now),
      )

      return { ...title, isInEarlyAccess }
    } else {
      return title
    }
  } else {
    reportWarning('episode', title)
    return
  }
}

function reportWarning(objType: string, title: CatalogTitle, err?: Error) {
  if (!isStagingEnvironment()) {
    logger().warn(`Unable to find a ${objType} from the federated graph for a Content Catalog title.`, { title }, err)
  }
}

async function handleDataSource(
  dataSource: string,
  pageContext: PageDataContextResult,
  page: Page,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  try {
    switch (dataSource) {
      case 'coming-soon':
        await handleComingSoon(pageContext)
        break
      case 'early-access':
        await handleEarlyAccess(pageContext, client)
        break
      case 'start-watching':
        await handleStartWatching(pageContext, page)
        break
      case 'all-movies':
        await handleAllMovies(pageContext, client)
        break
      case 'all-shows':
        await handleAllShows(pageContext, client)
        break
      default:
        logger().warn('Unknown data source', { dataSource })
    }
  } catch (error) {
    logger().error('Error handling data source', { dataSource }, error)
    pageContext.allQueriesSucceeded = false
  }
}

async function handleComingSoon(pageContext: PageDataContextResult): Promise<void> {
  const upcomingTabProjects = await getComingSoonCollection('FOCUS_SUMMARY')
  pageContext['coming-soon-tab'] = upcomingTabProjects
  if (upcomingTabProjects.length === 0) pageContext.allQueriesSucceeded = false
}

async function handleEarlyAccess(
  pageContext: PageDataContextResult,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  const sneakPeekProjects = await getSneakPeekEpisodes(client)
  pageContext['sneak-peeks'] = sneakPeekProjects
  if (!sneakPeekProjects) pageContext.allQueriesSucceeded = false
}

async function handleStartWatching(pageContext: PageDataContextResult, page: Page): Promise<void> {
  const startWatchingEpisodes = await getStartWatchingEpisodes({ analytics: { page } })
  pageContext['start-watching'] = startWatchingEpisodes
  if (startWatchingEpisodes.length === 0) pageContext.allQueriesSucceeded = false
}

async function handleAllMovies(
  pageContext: PageDataContextResult,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  try {
    const allMovies = await getAllMovies(client)
    pageContext['all-movies'] = allMovies ?? undefined
    if (!allMovies || allMovies.length === 0) {
      logger().warn('All movies query returned empty results', { dataSource: 'all-movies' })
      pageContext.allQueriesSucceeded = false
    }
  } catch (error) {
    logger().error('Failed to fetch all movies', { dataSource: 'all-movies' }, error)
    pageContext['all-movies'] = undefined
  }
}

async function handleAllShows(
  pageContext: PageDataContextResult,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<void> {
  try {
    const allShows = await getAllShows(client)
    pageContext['all-shows'] = allShows ?? undefined
    if (!allShows || allShows.length === 0) {
      logger().warn('All shows query returned empty results', { dataSource: 'all-shows' })
      pageContext.allQueriesSucceeded = false
    }
  } catch (error) {
    logger().error('Failed to fetch all shows', { dataSource: 'all-shows' }, error)
    pageContext['all-shows'] = undefined
  }
}
